import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { FaPrint, FaFileExcel, FaCopy } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const AccountantSubePaymentList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('2026-02-01');
  const [toDate, setToDate] = useState('2026-02-28');
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentData, setPaymentData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    setLoading(true);
    const engRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
    const socRef = ref(db, 'ekamwatapzp/SocietyRegistrations');

    let engData = {};
    let socData = {};
    let engSync = false;
    let socSync = false;

    const processResults = () => {
      if (engSync && socSync) {
        let combinedPayments = [];
        
        // Process Engineers
        Object.keys(engData).forEach(userId => {
          const user = engData[userId];
          if (user.payments) {
            Object.entries(user.payments).forEach(([payType, payData]) => {
                combinedPayments.push({
                  userId: userId,
                  userType: 'Engineer',
                  name: `${user.firstName || ''} ${user.middleName || ''} ${user.surname || user.lastName || ''}`.trim() || 'Unknown',
                  mobile: user.mobile || user.whatsappNo || 'N/A',
                  amount: parseFloat(payData.paidAmount || 0).toFixed(2),
                  date: payData.date || 'N/A',
                  rawDate: payData.date ? new Date(payData.date) : null,
                  type: payType
                });
            });
          }
        });

        // Process Societies
        Object.keys(socData).forEach(userId => {
          const user = socData[userId];
          if (user.payments) {
            Object.entries(user.payments).forEach(([payType, payData]) => {
                combinedPayments.push({
                  userId: userId,
                  userType: 'Society',
                  name: user.societyName || 'Unknown Society',
                  mobile: user.mobile || user.chairmanWhatsapp || 'N/A',
                  amount: parseFloat(payData.paidAmount || 0).toFixed(2),
                  date: payData.date || 'N/A',
                  rawDate: payData.date ? new Date(payData.date) : null,
                  type: payType
                });
            });
          }
        });

        // Sort by date descending
        combinedPayments.sort((a, b) => (b.rawDate || 0) - (a.rawDate || 0));
        
        // Re-assign IDs after sorting
        const finalData = combinedPayments.map((p, i) => ({ ...p, id: i + 1 }));
        
        setPaymentData(finalData);
        setLoading(false);
      }
    };

    const unsubEng = onValue(engRef, (snapshot) => {
      engData = snapshot.exists() ? snapshot.val() : {};
      engSync = true;
      processResults();
    });

    const unsubSoc = onValue(socRef, (snapshot) => {
      socData = snapshot.exists() ? snapshot.val() : {};
      socSync = true;
      processResults();
    });

    return () => {
      unsubEng();
      unsubSoc();
    };
  }, []);

  useEffect(() => {
    let filtered = paymentData.filter(item => {
      const itemDate = item.rawDate;
      const start = new Date(fromDate);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);

      const dateMatch = itemDate ? (itemDate >= start && itemDate <= end) : true;
      const searchMatch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          item.mobile.includes(searchTerm);
      
      return dateMatch && searchMatch;
    });
    setFilteredData(filtered);
  }, [paymentData, fromDate, toDate, searchTerm]);

  const handlePrint = () => {
    window.print();
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData.map(item => ({
      '#': item.id,
      'Name': item.name,
      'Mobile No.': item.mobile,
      'Payment Type': item.type,
      'Amount': item.amount,
      'Payment Date': item.date
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, "Payment_Details.xlsx");
  };

  const copyToClipboard = () => {
    const text = filteredData.map(item => `${item.id}\t${item.name}\t${item.mobile}\t${item.amount}\t${item.date}`).join('\n');
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-[98%] mx-auto bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        
        <div className="bg-[#ecedf3] text-gray-800 p-4 border-b border-gray-300 flex justify-between items-center print:hidden">
          <h1 className="text-xl font-medium">Payment Details</h1>
          <h2 className="text-sm font-medium text-gray-500 font-hindi">ठाणे जिल्हा परिषद / Payment Details</h2>
        </div>

        <div className="p-6 bg-white border-b border-gray-100 print:hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end max-w-4xl">
            <div className="flex flex-col gap-2">
              <label className="text-gray-700 font-bold text-sm">
                From Date <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-gray-700 font-bold text-sm">
                To Date <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <button className="bg-[#5c6bc0] hover:bg-blue-600 text-white text-sm font-medium px-8 py-2.5 rounded transition-colors shadow-sm mb-[2px]">
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 print:hidden">
            <div className="flex rounded-md shadow-sm">
              <button onClick={copyToClipboard} className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-4 py-2 border-r border-gray-500 rounded-l-md transition-colors font-bold uppercase">
                Copy
              </button>
              <button onClick={exportToExcel} className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-4 py-2 border-r border-gray-500 transition-colors font-bold uppercase">
                Excel
              </button>
              <button onClick={handlePrint} className="bg-gray-600 hover:bg-gray-700 text-white text-xs px-4 py-2 rounded-r-md transition-colors font-bold uppercase">
                Print
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500 font-medium">Search:</label>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500 w-48 shadow-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="w-full border-collapse bg-white text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-700 text-[13px] border-b border-gray-200">
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer w-20">#</th>
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer min-w-[250px]">Name</th>
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer w-40 text-center">Mobile No.</th>
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer w-40 text-center">Payment Type</th>
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer w-40 text-center">Amount</th>
                  <th className="px-4 py-4 text-left border-r border-gray-200 font-bold group cursor-pointer w-60 text-center">Payment Date</th>
                  <th className="px-4 py-4 text-left font-bold w-40 print:hidden text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading data...</td></tr>
                ) : filteredData.length > 0 ? (
                  filteredData.map((row, index) => (
                    <tr key={index} className="hover:bg-blue-50/50 transition-colors bg-white odd:bg-gray-50/30">
                      <td className="px-4 py-4 text-left border-r border-gray-100">{index + 1}</td>
                      <td className="px-4 py-4 text-left border-r border-gray-100 font-hindi text-gray-800">{row.name}</td>
                      <td className="px-4 py-4 text-center border-r border-gray-100 text-gray-600 font-bold">{row.mobile}</td>
                      <td className="px-4 py-4 text-center border-r border-gray-100 text-xs font-bold">
                        <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full">{row.type}</span>
                      </td>
                      <td className="px-4 py-4 text-center border-r border-gray-100 font-bold text-gray-800">₹{row.amount}</td>
                      <td className="px-4 py-4 text-center border-r border-gray-100 text-gray-500 font-bold italic">{row.date}</td>
                      <td className="px-4 py-4 text-center print:hidden">
                        <button 
                          onClick={() => {
                              const path = row.userType === 'Society' ? '/addceo/society-payment-details' : '/addceo/subhe-payment-details';
                              navigate(path, { state: { userId: row.userId, userType: row.userType } });
                          }}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-black px-5 py-2.5 rounded-2xl transition-all shadow-lg shadow-indigo-100 active:scale-95 uppercase tracking-widest border-2 border-white"
                        >
                          Payment Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No payment records found.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400 font-medium print:hidden">
            <p>Showing {filteredData.length} entries</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountantSubePaymentList;
